
import React from 'react';
import { Link } from 'react-router-dom';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SidebarMenuItemProps {
  title: string;
  path: string;
  icon: LucideIcon;
  isActive: boolean;
  collapsed: boolean;
  badgeCount?: number;
  badgeVariant?: 'default' | 'warning' | 'danger';
  dataOnboard?: string;
}

export const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
  title,
  path,
  icon: Icon,
  isActive,
  collapsed,
  badgeCount,
  badgeVariant,
  dataOnboard
}) => {
  const showBadge = typeof badgeCount === 'number' && badgeCount > 0;

  const badgeColor = badgeVariant === 'danger'
    ? 'bg-red-500 text-white'
    : badgeVariant === 'warning'
      ? 'bg-amber-500 text-white'
      : 'bg-blue-600 text-white';

  return (
    <Link 
      to={path} 
      className={cn(
        'relative flex items-center transition-all duration-200 ease-in-out rounded-md',
        collapsed ? 'justify-center px-2 py-3' : 'px-4 py-3',
        isActive ? 'bg-blue-50 text-primary font-medium' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      )}
      title={collapsed ? title : undefined}
      data-onboard={dataOnboard}
    >
      <Icon 
        size={20} 
        className={cn(
          "transition-colors duration-200", 
          isActive ? 'text-primary' : 'text-gray-500'
        )} 
      />
      {!collapsed && (
        <span className="ml-3 text-sm transition-opacity duration-200">
          {title}
        </span>
      )}
      {showBadge && (
        collapsed ? (
          <span
            className={cn(
              'absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ring-white',
              badgeVariant === 'danger' ? 'bg-red-500' : badgeVariant === 'warning' ? 'bg-amber-500' : 'bg-blue-600'
            )}
            aria-label={`${title}: ${badgeCount}`}
          />
        ) : (
          <span
            className={cn(
              'ml-auto inline-flex min-w-[20px] h-5 items-center justify-center rounded-full px-1.5 text-[11px] font-medium',
              badgeColor
            )}
            aria-label={`${title}: ${badgeCount}`}
          >
            {badgeCount! > 99 ? '99+' : badgeCount}
          </span>
        )
      )}
    </Link>
  );
};
